import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../Sidebar";
import Header from "../Header";
import API_BASE_URL from "../../config";
import "./Plans.css";

const formatMoney = (value) => {
  if (value === undefined || value === null) {
    return "Custom";
  }
  return `$${Number(value).toFixed(2)}`;
};

// Per-number channels offered as activation options during onboarding. Buying
// one starts a Stripe checkout and activates the workspace on payment.
const CHANNEL_OPTIONS = [
  {
    service: "whatsapp",
    name: "WhatsApp",
    price: 25,
    unit: "number",
    accent: "whatsapp",
    icon: "bi-whatsapp",
    tagline: "Message from your own WhatsApp number, linked by QR. Billed per number.",
    features: [
      "Send & receive from your own number (QR link)",
      "Unlimited messaging — no per-message fees",
      "Text, image, video & files",
      "No A2P registration",
      "Webhooks + API",
    ],
  },
  {
    service: "imessage",
    name: "iMessage",
    price: 49,
    unit: "number",
    accent: "imessage",
    icon: "bi-apple",
    tagline: "Blue-bubble iMessage with a fully managed Mac. Billed per number.",
    features: [
      "Blue-bubble iMessage",
      "Mac included — we host & manage it",
      "Add unlimited numbers",
      "Text, image, video & files",
      "Webhooks + API",
    ],
  },
];

// SMS section header shown on the onboarding page.
const SMS_SECTION = {
  icon: "bi-chat-dots-fill",
  tagline: "Send & receive SMS through your own Android phones. Pick a capacity tier.",
};

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [companyStatus, setCompanyStatus] = useState("");
  const [requiresCheckout, setRequiresCheckout] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const onboardingMode = params.has("checkout") || params.get("onboarding") === "1";

  const loadPlans = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem("userToken");
      if (!token) {
        setError("Please log in to choose a plan.");
        setPlans([]);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const [plansRes, subscriptionRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/plans`),
        axios.get(`${API_BASE_URL}/subscription`, { headers }),
      ]);
      setPlans(plansRes.data || []);
      const subscriptionData = subscriptionRes.data || {};
      setCurrentPlanId(subscriptionData.plan?.id || null);
      setCompanyStatus(subscriptionData.companyStatus || "");
      setRequiresCheckout(Boolean(subscriptionData.requiresCheckout));
      setError("");
    } catch (err) {
      console.error("Plans:load error", err);
      setError(err.response?.data?.error || "Unable to load plans.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    if (!requiresCheckout && onboardingMode && currentPlanId) {
      const timer = setTimeout(() => navigate("/dashboard", { replace: true }), 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [requiresCheckout, onboardingMode, currentPlanId, navigate]);

  useEffect(() => {
    if (!requiresCheckout) return undefined;
    const id = setInterval(() => loadPlans(true), 8000);
    return () => clearInterval(id);
  }, [requiresCheckout, loadPlans]);

  const handleCheckout = async (planId, skipTrial = false) => {
    try {
      setCheckoutPlanId(planId);
      setError("");
      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      const { data } = await axios.post(
        `${API_BASE_URL}/billing/checkout-session`,
        { plan_id: planId, skip_trial: skipTrial },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (data?.url) {
        try {
          const parsed = new URL(data.url);
          if (!["checkout.stripe.com", "billing.stripe.com"].includes(parsed.hostname)) {
            throw new Error("Unexpected checkout URL");
          }
          window.location.href = data.url;
        } catch { throw new Error("Invalid checkout URL."); }
      } else {
        throw new Error("Checkout link is unavailable.");
      }
    } catch (err) {
      console.error("Plans:checkout error", err);
      setError(err.response?.data?.error || err.message || "Unable to start checkout.");
    } finally {
      setCheckoutPlanId(null);
    }
  };

  // Start a per-number channel (WhatsApp/iMessage) checkout. Buying one activates
  // the workspace on payment; the /subscription poll then redirects to dashboard.
  const handleChannelCheckout = async (service) => {
    try {
      setCheckoutPlanId(service);
      setError("");
      const token = localStorage.getItem("userToken");
      if (!token) {
        throw new Error("Session expired. Please log in again.");
      }
      const { data } = await axios.post(
        `${API_BASE_URL}/services/${service}/subscribe`,
        { plan: "per_number", quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.url) {
        try {
          const parsed = new URL(data.url);
          if (!["checkout.stripe.com", "billing.stripe.com"].includes(parsed.hostname)) {
            throw new Error("Unexpected checkout URL");
          }
          window.location.href = data.url;
        } catch {
          throw new Error("Invalid checkout URL.");
        }
      } else if (data?.alreadyActive) {
        navigate("/dashboard");
      } else {
        throw new Error("Checkout link is unavailable.");
      }
    } catch (err) {
      console.error("Plans:channel checkout error", err);
      setError(err.response?.data?.error || err.message || "Unable to start checkout.");
    } finally {
      setCheckoutPlanId(null);
    }
  };

  const renderPlanCard = (plan) => {
    const isCurrent = plan.id === currentPlanId;
    const isProcessing = checkoutPlanId === plan.id;
    return (
      <div key={plan.id} className={`plan-card ${isCurrent ? "plan-card-active" : ""}`}>
        <div className="plan-card-header">
          <div>
            <h5 className="plan-title">{plan.name}</h5>
            <p className="plan-price">
              {formatMoney(plan.price)} <span>/{plan.billing_cycle || "monthly"}</span>
            </p>
          </div>
          {isCurrent && <span className="plan-pill">Current</span>}
        </div>
        <ul className="plan-features">
          <li>{plan.devices_allowed || "Unlimited"} devices</li>
          <li>{plan.users_allowed || "Unlimited"} users</li>
          <li>Unlimited SMS volume</li>
          <li>{plan.priority_support ? "Priority support" : "Standard support"}</li>
          {Array.isArray(plan.features) && plan.features.map((feature, idx) => <li key={idx}>{feature}</li>)}
        </ul>
        {isCurrent ? (
          <button className="btn w-100 btn-outline-secondary" disabled>Current Plan</button>
        ) : (
          <div className="plan-buttons">
            <button
              className="btn w-100 btn-primary"
              disabled={isProcessing}
              onClick={() => handleCheckout(plan.id, false)}
            >
              {isProcessing ? "Redirecting..." : "Start 14-Day Trial"}
            </button>
            <button
              className="btn w-100 btn-outline-primary btn-skip-trial"
              disabled={isProcessing}
              onClick={() => handleCheckout(plan.id, true)}
            >
              Skip Trial &amp; Get Full Access
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderChannelCard = (ch) => {
    const isProcessing = checkoutPlanId === ch.service;
    return (
      <div key={ch.service} className={`plan-card plan-card--${ch.accent}`}>
        <div className="plan-card-header">
          <div>
            <h5 className="plan-title">{ch.name}</h5>
            <p className="plan-price">
              {formatMoney(ch.price)} <span>/{ch.unit} / month</span>
            </p>
          </div>
        </div>
        <ul className="plan-features">
          {ch.features.map((feature, idx) => (
            <li key={idx}>{feature}</li>
          ))}
        </ul>
        <div className="plan-buttons">
          <button
            className={`btn w-100 plan-cta plan-cta--${ch.accent}`}
            disabled={isProcessing}
            onClick={() => handleChannelCheckout(ch.service)}
          >
            {isProcessing ? "Redirecting..." : `Get ${ch.name}`}
          </button>
        </div>
      </div>
    );
  };

  const renderSectionHead = (accent, icon, title, tagline) => (
    <div className={`plan-section-head plan-section-head--${accent}`}>
      <span className="plan-section-icon">
        <i className={`bi ${icon}`} />
      </span>
      <div className="plan-section-meta">
        <h5>{title}</h5>
        <p>{tagline}</p>
      </div>
    </div>
  );

  return (
    <div className="plans-container">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="container mt-4">
          <div className="plans-header">
            <div>
              <p className="eyebrow">Pairfon Plans</p>
              <h4>Find the right package for your gateways</h4>
              <p className="text-muted">
                Every plan includes full API access. Upgrade anytime - payment is completed securely at checkout.
              </p>
            </div>
            {!onboardingMode && (
              <button className="btn btn-outline-secondary" onClick={() => navigate("/billing")}>
                Back to Billing
              </button>
            )}
          </div>

          {requiresCheckout ? (
            <div className="plans-alert warning">
              <div>
                <strong>Activate your workspace.</strong>
                <p className="mb-0">
                  Your email is verified. Complete secure checkout to start the Pairfon trial. We'll hold your workspace
                  until payment is completed.
                </p>
              </div>
            </div>
          ) : companyStatus ? (
            <div className="plans-alert success">
              <div>
                <strong>Subscription ready.</strong>
                <p className="mb-0">You can return to the dashboard anytime.</p>
              </div>
              <button className="btn btn-primary" onClick={() => navigate("/dashboard")}>
                Go to Dashboard
              </button>
            </div>
          ) : null}

          {error && <div className="alert alert-danger">{error}</div>}
          {loading ? (
            <div className="text-center py-5">Loading plans...</div>
          ) : (
            <div className="plans-sections">
              <section className="plan-section">
                {renderSectionHead("sms", SMS_SECTION.icon, "SMS", SMS_SECTION.tagline)}
                <div className="plans-grid">{plans.map(renderPlanCard)}</div>
              </section>
              <section className="plan-section">
                {renderSectionHead(
                  "channels",
                  "bi-chat-dots-fill",
                  "WhatsApp & iMessage",
                  "Message from your own number, billed per number. Add either or both."
                )}
                <div className="plans-grid">{CHANNEL_OPTIONS.map(renderChannelCard)}</div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
